import axios from 'axios';
import { getStoredToken } from './authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('grit_token');
      localStorage.removeItem('grit_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getDirectionDashboardStats = () => api.get('/dashboard/direccion-stats');

// Clients
export const getClients = (params) => api.get('/clients', { params });
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Operational Cases (replaces Movements)
export const getOperationalCases = (params) => api.get('/operational-cases', { params });
export const getOperationalCase = (id) => api.get(`/operational-cases/${id}`);
export const createOperationalCase = (data) => api.post('/operational-cases', data);
export const updateOperationalCase = (id, data) => api.put(`/operational-cases/${id}`, data);
export const deleteOperationalCase = (id) => api.delete(`/operational-cases/${id}`);
export const updateCaseCheck = (id, data) => api.put(`/operational-cases/${id}/check`, data);

// Cash Sessions (new)
export const getCashSessions = (limit = 60, fecha_desde, fecha_hasta) => {
  const params = { limit };
  if (fecha_desde) params.fecha_desde = fecha_desde;
  if (fecha_hasta) params.fecha_hasta = fecha_hasta;
  return api.get('/cash-sessions', { params });
};
export const getTodayCashSession = () => api.get('/cash-sessions/today');
export const openCashSession = (data) => api.post('/cash-sessions/open', data);
export const closeCashSession = (data) => api.post('/cash-sessions/close', data);

// Cash Transactions (new - row by row)
export const getCashTransactions = (params) => api.get('/cash-transactions', { params });
export const createCashTransaction = (data) => api.post('/cash-transactions', data);
export const deleteCashTransaction = (id) => api.delete(`/cash-transactions/${id}`);

// Backward compat cash register
export const getTodayCashRegister = () => api.get('/cash-register/today');
export const getCashRegisterHistory = (limit = 30) => api.get('/cash-register/history', { params: { limit } });

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const createTask = (data) => api.post('/tasks', data);
export const updateTaskStatus = (id, status) => api.put(`/tasks/${id}/status`, null, { params: { status } });
export const updateTaskDates = (id, data) => api.put(`/tasks/${id}/dates`, data);
export const addTaskComment = (id, comment) => api.post(`/tasks/${id}/comment`, null, { params: { comment } });
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// Leads
export const getLeads = (params) => api.get('/leads', { params });
export const createLead = (data) => api.post('/leads', data);
export const updateLeadStatus = (id, status) => api.put(`/leads/${id}/status`, null, { params: { status } });
export const addLeadContact = (id, data) => api.post(`/leads/${id}/contact`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);

// Rejected Payments
export const getRejectedPayments = (params) => api.get('/rejected-payments', { params });
export const createRejectedPayment = (data) => api.post('/rejected-payments', data);
export const updateRejectedPayment = (id, data) => api.put(`/rejected-payments/${id}`, data);
export const updateRejectedPaymentStatus = (id, status, observations) =>
  api.put(`/rejected-payments/${id}/status`, { status, observations });
export const addRejectedPaymentContact = (id, result, observations) =>
  api.post(`/rejected-payments/${id}/contact`, { result, observations });
export const deleteRejectedPayment = (id) => api.delete(`/rejected-payments/${id}`);
export const syncRejectedPaymentsVpos = () => api.post('/rejected-payments/sync-virtualpos');

// Products
export const getProducts = (lowStockOnly = false) => api.get('/products', { params: { low_stock_only: lowStockOnly } });
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const updateProductStock = (id, quantity) => api.put(`/products/${id}/stock`, null, { params: { quantity } });
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// Daily Notes
export const getTodayNotes = () => api.get('/daily-notes/today');
export const createDailyNote = (data) => api.post('/daily-notes', data);

// Bonus
export const getCurrentBonusConfig = () => api.get('/bonus-config/current');
export const createBonusConfig = (data) => api.post('/bonus-config', data);
export const getBonusTracking = (params) => api.get('/bonus/tracking', { params });

// Reports
export const getDailyReport = (date) => api.get(`/reports/daily/${date}`);

// Users
export const getUsers = () => api.get('/users');

// Export URLs
export const exportClients = () => `${API_BASE}/export/clients`;
export const exportDailyReport = (date) => `${API_BASE}/export/daily-report/${date}`;
export const exportLeads = () => `${API_BASE}/export/leads`;
export const exportRejectedPayments = () => `${API_BASE}/export/rejected-payments`;
export const exportInventory = () => `${API_BASE}/export/inventory`;
export const exportControlMensual = (fechaDesde, fechaHasta) => {
  let url = `${API_BASE}/export/control-mensual`;
  const params = [];
  if (fechaDesde) params.push(`fecha_desde=${fechaDesde}`);
  if (fechaHasta) params.push(`fecha_hasta=${fechaHasta}`);
  if (params.length) url += `?${params.join('&')}`;
  return url;
};

// Virtual POS
export const getVirtualPosPlans = () => api.get('/virtualpos/plans');
export const syncVirtualPosSubscriptions = () => api.post('/virtualpos/sync-subscriptions');

// Data Migration
export const migrateAll = () => api.post('/migrate/all');
export const migrateClients = () => api.post('/migrate/clients');
export const migrateRejectedPayments = () => api.post('/migrate/rejected-payments');
export const migrateProducts = () => api.post('/migrate/products');

// Calendar
export const getCalendarEvents = (params) => api.get('/calendar/events', { params });
export const createCalendarEvent = (data) => api.post('/calendar/events', data);
export const updateCalendarEvent = (id, data) => api.put(`/calendar/events/${id}`, data);
export const deleteCalendarEvent = (id) => api.delete(`/calendar/events/${id}`);

// Shopify Orders
export const getShopifyOrders = () => api.get('/shopify-orders');
export const createShopifyOrder = (data) => api.post('/shopify-orders', data);
export const updateShopifyOrder = (id, data) => api.put(`/shopify-orders/${id}`, data);
export const syncShopifyOrders = () => api.post('/shopify-orders/sync');

// Shopify Store Config
export const getShopifyStoreUrl = () => api.get('/config/shopify-store');
export const setShopifyStoreUrl = (store_url) => api.post('/config/shopify-store', { store_url });

// Custom Class Types (Disciplines)
export const getCustomClassTypes = () => api.get('/config/class-types');
export const addCustomClassType = (label) => api.post('/config/class-types', { label });
export const deleteCustomClassType = (value) => api.delete(`/config/class-types/${value}`);

// Custom Plan Types
export const getCustomPlanTypes = () => api.get('/config/plan-types');
export const addCustomPlanType = (name) => api.post('/config/plan-types', { name });
export const deleteCustomPlanType = (name) => api.delete(`/config/plan-types/${encodeURIComponent(name)}`);

// Costos Fijos
export const getCostosFijos = () => api.get('/config/costos-fijos');
export const setCostosFijos = (monto, detalle) => api.post('/config/costos-fijos', { monto, detalle });

// Shopify Legacy
export const getShopifyLeadsSummary = () => api.get('/shopify/leads-summary');

// Shopify Customers → Leads Sync
export const syncShopifyCustomers = () => api.post('/shopify-customers/sync');

// Virtual POS Enhanced
export const getVposDashboard = () => api.get('/virtualpos/dashboard');
export const syncVposComprehensive = () => api.post('/virtualpos/sync-all');
export const getProjectedRevenue = () => api.get('/virtualpos/projected-revenue');

// Task Comments/Notes - already exported above as addTaskComment

// Seed
export const seedData = () => api.post('/seed');
export const initializeAll = () => api.post('/initialize-all');

// Excel Upload
export const uploadExcelPreview = (file, uploadType) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/upload/preview?upload_type=${uploadType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const uploadExcelConfirm = (payload) => api.post('/upload/confirm', payload);

// Professor Hours
export const getProfessors = () => api.get('/professors');
export const createProfessor = (data) => api.post('/professors', data);
export const updateProfessor = (id, data) => api.put(`/professors/${id}`, data);
export const deleteProfessor = (id) => api.delete(`/professors/${id}`);
export const getProfessorRates = (params) => api.get('/professor-rates', { params });
export const upsertProfessorRate = (data) => api.post('/professor-rates', data);
export const getProfessorHours = (params) => api.get('/professor-hours', { params });
export const createProfessorHour = (data) => api.post('/professor-hours', data);
export const updateProfessorHour = (id, data) => api.put(`/professor-hours/${id}`, data);
export const deleteProfessorHour = (id) => api.delete(`/professor-hours/${id}`);
export const getProfessorSummary = (month) => api.get('/professor-summary', { params: { month } });
export const updateProfessorSummary = (professorId, month, data) => api.put(`/professor-summary/${professorId}`, data, { params: { month } });

// Internal Notes
export const getInternalNotes = () => api.get('/internal-notes');
export const createInternalNote = (data) => api.post('/internal-notes', data);
export const updateInternalNote = (id, data) => api.put(`/internal-notes/${id}`, data);
export const deleteInternalNote = (id) => api.delete(`/internal-notes/${id}`);

// Gantt Tasks
export const getGanttTasks = () => api.get('/gantt-tasks');
export const createGanttTask = (data) => api.post('/gantt-tasks', data);
export const updateGanttTask = (id, data) => api.put(`/gantt-tasks/${id}`, data);
export const deleteGanttTask = (id) => api.delete(`/gantt-tasks/${id}`);

// User Management
export const getAllUsers = () => api.get('/users/all');
export const createUserManagement = (data) => api.post('/users/create', data);
export const updateUserManagement = (id, data) => api.put(`/users/${id}`, data);
export const deleteUserManagement = (id) => api.delete(`/users/${id}`);

// Expenses (Flujo)
export const getExpenses = (month) => api.get('/expenses', { params: { month } });
export const getExpensesSummary = (month) => api.get('/expenses/summary', { params: { month } });
export const getExpensesBalance = (month) => api.get('/expenses/balance', { params: { month } });
export const createExpense = (data) => api.post('/expenses', data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);
export const seedHistoricalExpenses = () => api.post('/expenses/seed-historical');
export const exportExpenses = (month) => api.get('/expenses/export', { params: { month }, responseType: 'blob' });

// Cash Reports
export const getCashConsolidatedReport = (params) => api.get('/cash-reports/consolidated', { params });
export const getCashDetailReport = (params) => api.get('/cash-reports/detail', { params });
export const exportCashReport = (fechaDesde, fechaHasta) => {
  let url = `${API_BASE}/export/cash-report`;
  const params = [];
  if (fechaDesde) params.push(`fecha_desde=${fechaDesde}`);
  if (fechaHasta) params.push(`fecha_hasta=${fechaHasta}`);
  if (params.length) url += `?${params.join('&')}`;
  return url;
};

export default api;
